import { useEffect, useState } from 'react';
import { apiRequest } from '../../services/apiClient';

export interface ChatbotLink {
  label: string;
  url: string;
}

export interface ChatbotEntry {
  id: number;
  question: string;
  answer: string;
  keywords: string;
  links: ChatbotLink[];
  quickLabel: string | null;
}

export interface AdminChatbotEntry extends ChatbotEntry {
  sortOrder: number;
  isPublished: boolean;
}

export type ChatbotEntryInput = Omit<AdminChatbotEntry, 'id' | 'sortOrder'>;

// 챗봇을 처음 열 때 한 번만 불러오고, 관리자가 수정하면 invalidate로 다시 불러온다
let entriesRequest: Promise<ChatbotEntry[]> | null = null;
const listeners = new Set<() => void>();

function fetchChatbotEntries(): Promise<ChatbotEntry[]> {
  if (!entriesRequest) {
    entriesRequest = apiRequest<ChatbotEntry[]>('/api/chatbot').catch((err) => {
      entriesRequest = null;
      throw err;
    });
  }
  return entriesRequest;
}

export function invalidateChatbotEntries() {
  entriesRequest = null;
  listeners.forEach((listener) => listener());
}

// enabled가 처음 true가 될 때 불러온다. 실패하면 빈 목록(기본 안내만 동작)으로 둔다
export function useChatbotEntries(enabled: boolean): ChatbotEntry[] | null {
  const [entries, setEntries] = useState<ChatbotEntry[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const load = () => {
      fetchChatbotEntries()
        .then((list) => active && setEntries(list))
        .catch(() => active && setEntries([]));
    };
    load();
    listeners.add(load);
    return () => {
      active = false;
      listeners.delete(load);
    };
  }, [enabled]);

  return entries;
}
